import { Component, computed, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import moment from 'moment';
import { catchError, of } from 'rxjs';
import { MonthPagerComponent } from 'src/app/shared/components/month-pager/month-pager.component';
import { MaterialModule } from 'src/app/shared/material.module';
import { NotificationHistoryEntry } from './models/notification-history-entry';
import { TimeLoggingNotificationService } from 'src/app/shared/services/time-logging-notification.service';
import { NotificationHistoryComponent } from './notification-history/notification-history.component';
import { NotificationSetupComponent } from './notification-setup/notification-setup.component';

@Component({
  selector: 'app-notification-management',
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    MaterialModule,
    NotificationHistoryComponent,
    NotificationSetupComponent,
    MonthPagerComponent,
  ],
  templateUrl: './notification-management.component.html',
  styleUrl: './notification-management.component.scss',
})
export class NotificationManagementComponent {
  private notificationService = inject(TimeLoggingNotificationService);
  private setupComponent = viewChild(NotificationSetupComponent);

  hasPendingChanges = computed(() => this.setupComponent()?.hasChanges() ?? false);

  private destroyRef = inject(DestroyRef);

  selectedMonth = signal<moment.Moment>(moment());
  selectedMonthLabel = computed(() => this.selectedMonth().format('MMMM YYYY').toUpperCase());
  history = signal<NotificationHistoryEntry[]>([]);

  availableMonths = toSignal(
    this.notificationService.getAvailableMonths().pipe(catchError(() => of(null))),
    { initialValue: null },
  );

  constructor() {
    this.refreshHistory();
  }

  onMonthChange(month: moment.Moment): void {
    this.selectedMonth.set(month);
    this.refreshHistory();
  }

  onSetupSaved(): void {
    this.refreshHistory();
  }

  private refreshHistory(): void {
    this.notificationService
      .getNotificationHistory(this.selectedMonth().format('YYYY-MM'))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(history => this.history.set(history));
  }
}
